import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/ui";
import { Button } from "@/components/ui/UIElements/Button";
import { Input } from "@/components/ui/UIElements/Input";
import { SearchField } from "@/components/ui/UIElements/FormField";
import { NativeSelect } from "@/components/ui/UIElements/Select";
import Modal from "../../ui/UIElements/Modal";
import { PhoneInputSimple } from "../../ui/UIElements/PhoneInput";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import {
  useAgentTemplates,
  useRefreshAgentTemplates,
  useSyncAgentTemplates,
  useSendAgentTemplate,
} from "@/hooks/useAiAgents";

const STATUS_BADGE_CLASSES = {
  APPROVED: "bg-[var(--success-bg)] text-success border-success",
  PENDING: "bg-[var(--warning-bg)] text-warning border-warning",
  IN_APPEAL: "bg-[var(--warning-bg)] text-warning border-warning",
  REINSTATED: "bg-[var(--warning-bg)] text-warning border-warning",
  REJECTED: "bg-[var(--error-bg)] text-destructive border-destructive",
  DISABLED: "bg-[var(--error-bg)] text-destructive border-destructive",
  FLAGGED: "bg-[var(--error-bg)] text-destructive border-destructive",
  PAUSED: "bg-[var(--error-bg)] text-destructive border-destructive",
  LIMIT_EXCEEDED: "bg-[var(--error-bg)] text-destructive border-destructive",
  LOCKED: "bg-[var(--error-bg)] text-destructive border-destructive",
  ERROR: "bg-[var(--error-bg)] text-destructive border-destructive",
  NOT_SYNCED: "bg-card/60 text-muted-foreground border-border",
  ARCHIVED: "bg-card/60 text-muted-foreground border-border",
  DELETED: "bg-card/60 text-muted-foreground border-border",
  PENDING_DELETION: "bg-card/60 text-muted-foreground border-border",
  DELETING: "bg-card/60 text-muted-foreground border-border",
};

const QUALITY_BADGE_CLASSES = {
  GREEN: "bg-[var(--success-bg)] text-success border-success",
  YELLOW: "bg-[var(--warning-bg)] text-warning border-warning",
  RED: "bg-[var(--error-bg)] text-destructive border-destructive",
  UNKNOWN: "bg-card/60 text-muted-foreground border-border",
};

const REJECTION_REASON_VISIBLE_ON = new Set([
  "REJECTED",
  "FLAGGED",
  "PAUSED",
  "DISABLED",
  "LIMIT_EXCEEDED",
  "ERROR",
]);

// Supported dashboard locales — these are the only languages operators can
// author templates in today (see apps/dashboard/src/i18n/index.js). Labels
// come from the i18n namespace (templates.filters.languages.*).
const SUPPORTED_LANGUAGES = ["en", "es", "fr", "ar"];

/**
 * Extract the number of {{N}} placeholders from a BODY component text.
 * Returns the count so we can render one input per placeholder.
 */
const countBodyPlaceholders = (bodyText) => {
  if (!bodyText) return 0;
  const matches = bodyText.match(/\{\{\d+\}\}/g);
  return matches ? matches.length : 0;
};

/** A template can be sent when it is APPROVED + MARKETING. */
const isSendable = (tpl) =>
  tpl?.sync_status === "APPROVED" && tpl?.category === "MARKETING";

const statusBadgeClass = (status) =>
  STATUS_BADGE_CLASSES[status] ||
  "bg-card/60 text-muted-foreground border-border";

const qualityBadgeClass = (score) =>
  QUALITY_BADGE_CLASSES[score] || QUALITY_BADGE_CLASSES.UNKNOWN;

/** Phone number (with country code, no '+') must be 10–15 digits per the API spec. */
const isPhoneValid = (phone) => /^\d{10,15}$/.test(phone);

/**
 * Modal form to send a marketing template message to a phone number.
 */
const SendTemplateModal = ({
  isOpen,
  templateId,
  templateName,
  bodyText,
  placeholderCount,
  isPending,
  onSend,
  onClose,
  t,
}) => {
  const [phone, setPhone] = useState("");
  const [params, setParams] = useState(() =>
    new Array(placeholderCount).fill(""),
  );

  const updateParam = (index, value) =>
    setParams((prev) => prev.map((v, i) => (i === index ? value : v)));

  // Live preview: replace {{N}} placeholders with the current param values.
  const previewText = useMemo(() => {
    if (!bodyText) return null;
    return params.reduce(
      (text, val, idx) =>
        text.replace(`{{${idx + 1}}}`, val || `{{${idx + 1}}}`),
      bodyText,
    );
  }, [bodyText, params]);

  // Strip the leading "+" so the value matches the API format (e.g. 573008159947).
  const normalizedPhone = phone?.replace(/^\+/, "").trim() || "";
  const allParamsFilled =
    placeholderCount === 0 || params.every((p) => p.trim() !== "");
  const isValid = isPhoneValid(normalizedPhone) && allParamsFilled;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isValid) return;
    onSend(
      templateId,
      normalizedPhone,
      params.filter((p) => p !== ""),
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("templates.send.modalTitle")}
      subtitle={templateName}
      maxWidth="max-w-md"
    >
      <form
        onSubmit={handleSubmit}
        className="ordering-ai-agent-modal-content space-y-4"
      >
        <div>
          <PhoneInputSimple
            value={phone}
            onChange={(val) => setPhone(val || "")}
            label={t("templates.send.phoneLabel")}
            placeholder={t("templates.send.phonePlaceholder")}
            size="md"
          />
        </div>
        {placeholderCount > 0 && (
          <div className="space-y-3">
            <p className="text-muted-foreground text-sm font-medium">
              {t("templates.send.parametersLabel")}
            </p>
            {params.map((val, idx) => (
              <Input
                key={idx}
                type="text"
                value={val}
                onChange={(e) => updateParam(idx, e.target.value)}
                placeholder={`${t("templates.send.parameterPlaceholder")} {{${idx + 1}}}`}
                aria-label={`${t("templates.send.parameterPlaceholder")} {{${idx + 1}}}`}
                className="w-full"
              />
            ))}
          </div>
        )}
        {previewText && (
          <div>
            <p className="text-muted-foreground mb-1 text-sm font-medium">
              {t("templates.send.previewLabel")}
            </p>
            <div className="border-border bg-background/40 text-foreground rounded-lg border p-3 text-sm whitespace-pre-wrap">
              {previewText}
            </div>
          </div>
        )}
        <div className="border-border border-t pt-4">
          <Button
            type="submit"
            emphasis="solid"
            intent="success"
            size="touch"
            disabled={!isValid || isPending}
            className="w-full"
          >
            <Icon name="Send" className="me-2 h-4 w-4" />
            {isPending
              ? t("templates.send.sending")
              : t("templates.send.submit")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

/**
 * AgentTemplatesSection — read-only list of WhatsApp message templates attached
 * to the agent's WABA, with a "refresh from Meta" button.
 * Only mount this for agents where provider === "whatsapp".
 */
const AgentTemplatesSection = ({ agent, showHeader = true }) => {
  const { t } = useTranslation("aiAgents");
  const toast = useToast();

  const {
    data: templates = [],
    isLoading,
    isError,
    refetch,
  } = useAgentTemplates(agent?.id);

  const refreshMutation = useRefreshAgentTemplates();
  const syncMutation = useSyncAgentTemplates();
  const sendMutation = useSendAgentTemplate();

  // Track which template id has the send form open (null = none).
  const [sendFormOpenFor, setSendFormOpenFor] = useState(null);

  const [nameFilter, setNameFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [languageFilter, setLanguageFilter] = useState("");

  // Unique statuses actually present in the current dataset — so the dropdown
  // only offers options the user can meaningfully pick.
  const availableStatuses = useMemo(() => {
    const set = new Set();
    for (const tpl of templates) {
      if (tpl?.sync_status) set.add(tpl.sync_status);
    }
    return Array.from(set).sort();
  }, [templates]);

  const filteredTemplates = useMemo(() => {
    const needle = nameFilter.trim().toLowerCase();
    return templates.filter((tpl) => {
      if (needle && !tpl?.name?.toLowerCase().includes(needle)) return false;
      if (statusFilter && tpl?.sync_status !== statusFilter) return false;
      if (languageFilter && tpl?.language !== languageFilter) return false;
      return true;
    });
  }, [templates, nameFilter, statusFilter, languageFilter]);

  const handleRefresh = async () => {
    try {
      await refreshMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(t("templates.refreshSuccess"), toast);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("templates.refreshError"),
        defaultMessage: t("templates.refreshError"),
      });
    }
  };

  const handleSync = async () => {
    try {
      await syncMutation.mutateAsync({ agentId: agent.id });
      handleApiSuccess(t("templates.syncSuccess"), toast);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("templates.syncError"),
        defaultMessage: t("templates.syncError"),
      });
    }
  };

  const handleSend = async (templateId, to, bodyParameters) => {
    try {
      await sendMutation.mutateAsync({
        agentId: agent.id,
        templateId,
        to,
        bodyParameters,
      });
      handleApiSuccess(t("templates.send.success"), toast);
      setSendFormOpenFor(null);
    } catch (error) {
      handleApiError(error, toast, {
        title: t("templates.send.error"),
        defaultMessage: t("templates.send.error"),
      });
    }
  };

  const formatStatusLabel = (status) =>
    t(`templates.status.${status}`, { defaultValue: status });
  const formatQualityLabel = (score) =>
    t(`templates.qualityScore.${score}`, { defaultValue: score });
  const formatRejectionLabel = (reason) =>
    t(`templates.rejectionReason.${reason}`, { defaultValue: reason });

  return (
    <div className="ordering-ai-agent-panel ordering-ai-agent-templates">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        {showHeader ? (
          <div>
            <h3 className="text-foreground text-lg font-semibold">
              {t("templates.title")}
            </h3>
            <p className="text-muted-foreground mt-1 text-sm">
              {t("templates.description")}
            </p>
          </div>
        ) : (
          <div />
        )}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            emphasis="outline"
            intent="neutral"
            size="touch"
            onClick={handleRefresh}
            disabled={
              refreshMutation.isPending || syncMutation.isPending || isLoading
            }
          >
            <Icon
              name="RefreshCw"
              className={`me-2 h-4 w-4 ${
                refreshMutation.isPending ? "animate-spin" : ""
              }`}
            />
            {refreshMutation.isPending
              ? t("templates.refreshing")
              : t("templates.refreshButton")}
          </Button>
          <Button
            type="button"
            emphasis="solid"
            intent="brand"
            size="touch"
            onClick={handleSync}
            disabled={
              syncMutation.isPending || refreshMutation.isPending || isLoading
            }
          >
            <Icon
              name="UploadCloud"
              className={`me-2 h-4 w-4 ${
                syncMutation.isPending ? "animate-spin" : ""
              }`}
            />
            {syncMutation.isPending
              ? t("templates.syncing")
              : t("templates.syncButton")}
          </Button>
        </div>
      </div>

      {/* Local filters — operate on the already-fetched list, no API calls. */}
      {!isLoading && !isError && templates.length > 0 && (
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
          <SearchField
            value={nameFilter}
            onValueChange={setNameFilter}
            clearLabel={t("templates.filters.nameLabel")}
            placeholder={t("templates.filters.namePlaceholder")}
            aria-label={t("templates.filters.nameLabel")}
            className="w-full"
            containerClassName="flex-1"
          />
          <NativeSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label={t("templates.filters.statusLabel")}
          >
            <option value="">{t("templates.filters.statusAll")}</option>
            {availableStatuses.map((status) => (
              <option key={status} value={status}>
                {formatStatusLabel(status)}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
            aria-label={t("templates.filters.languageLabel")}
          >
            <option value="">{t("templates.filters.languageAll")}</option>
            {SUPPORTED_LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {t(`templates.filters.languages.${lang}`)}
              </option>
            ))}
          </NativeSelect>
        </div>
      )}

      {isLoading ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="border-border bg-card h-16 animate-pulse rounded-lg border"
            />
          ))}
        </div>
      ) : isError ? (
        <div className="border-destructive rounded-lg border bg-[var(--error-bg)] p-4">
          <p className="text-destructive text-sm">{t("templates.loadError")}</p>
          <Button
            type="button"
            emphasis="outline"
            intent="danger"
            size="sm"
            onClick={() => refetch()}
            className="mt-2"
          >
            <Icon name="RefreshCw" className="me-1.5 h-3 w-3" />
            {t("templates.retry")}
          </Button>
        </div>
      ) : templates.length === 0 ? (
        <div className="border-border bg-card/40 rounded-lg border border-dashed p-6 text-center">
          <p className="text-muted-foreground text-sm">
            {t("templates.empty")}
          </p>
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="border-border bg-card/40 rounded-lg border border-dashed p-6 text-center">
          <p className="text-muted-foreground text-sm">
            {t("templates.filters.emptyFiltered")}
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {filteredTemplates.map((tpl) => {
            const status = tpl.sync_status;
            const showReason =
              tpl.reason_status &&
              tpl.reason_status !== "NONE" &&
              REJECTION_REASON_VISIBLE_ON.has(status);
            const bodyComponent = Array.isArray(tpl.components)
              ? tpl.components.find((c) => c?.type === "BODY")
              : null;
            const bodyText = bodyComponent?.text;
            const footerComponent = Array.isArray(tpl.components)
              ? tpl.components.find((c) => c?.type === "FOOTER")
              : null;
            const footerText =
              typeof footerComponent?.text === "string"
                ? footerComponent.text
                : null;

            return (
              <li
                key={`${tpl.id}-${tpl.language}`}
                className="border-border bg-card/60 rounded-lg border p-4"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p
                        className="text-foreground truncate font-mono text-sm font-medium"
                        title={tpl.name}
                      >
                        {tpl.name}
                      </p>
                      {tpl.category && (
                        <span className="border-border bg-background/60 text-muted-foreground rounded border px-2 py-0.5 text-xs">
                          {tpl.category}
                        </span>
                      )}
                      <span className="border-border bg-background/60 text-muted-foreground rounded border px-2 py-0.5 text-xs uppercase">
                        {tpl.language}
                      </span>
                    </div>
                    {bodyText && (
                      <p className="border-border bg-background/40 text-foreground mt-2 rounded border p-2 text-xs whitespace-pre-wrap">
                        {bodyText}
                      </p>
                    )}
                    {footerText && (
                      <p className="text-muted-foreground mt-1 text-xs whitespace-pre-wrap italic">
                        {footerText}
                      </p>
                    )}
                    {showReason && (
                      <p className="text-destructive mt-1 text-xs">
                        {t("templates.columns.reason")}:{" "}
                        {formatRejectionLabel(tpl.reason_status)}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                    <span
                      className={`rounded border px-2 py-0.5 text-xs font-medium ${statusBadgeClass(
                        status,
                      )}`}
                    >
                      {formatStatusLabel(status)}
                    </span>
                    {tpl.quality_score && (
                      <span
                        className={`rounded border px-2 py-0.5 text-xs font-medium ${qualityBadgeClass(
                          tpl.quality_score,
                        )}`}
                      >
                        {t("templates.columns.qualityScore")}:{" "}
                        {formatQualityLabel(tpl.quality_score)}
                      </span>
                    )}
                    {isSendable(tpl) && (
                      <Button
                        type="button"
                        emphasis="outline"
                        intent="success"
                        size="sm"
                        onClick={() => setSendFormOpenFor(tpl.id)}
                      >
                        <Icon name="Send" className="me-1 h-3 w-3" />
                        {t("templates.send.button")}
                      </Button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* Send template modal — rendered once, outside the list */}
      {sendFormOpenFor != null &&
        (() => {
          const tpl = templates.find((t) => t.id === sendFormOpenFor);
          if (!tpl || !isSendable(tpl)) return null;
          const body = Array.isArray(tpl.components)
            ? tpl.components.find((c) => c?.type === "BODY")
            : null;
          return (
            <SendTemplateModal
              isOpen
              templateId={tpl.id}
              templateName={tpl.name}
              bodyText={body?.text || ""}
              placeholderCount={countBodyPlaceholders(body?.text)}
              isPending={sendMutation.isPending}
              onSend={handleSend}
              onClose={() => setSendFormOpenFor(null)}
              t={t}
            />
          );
        })()}
    </div>
  );
};

export default AgentTemplatesSection;
